import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { Card, CardContent } from '../../components/ui/Card';
import { Link } from 'react-router-dom';

export const Home = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-24 pb-32 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -30 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-black uppercase tracking-widest border border-indigo-100">
              <SafeIcon name="Zap" /> Psykososialt Arbeidsmiljø 2.0
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-slate-900 leading-[0.95] tracking-tighter uppercase">
              Styrk det <span className="text-indigo-600">psykososiale</span> arbeidsmiljøet
            </h1>
            <p className="text-xl text-slate-500 leading-relaxed max-w-xl font-medium">
              Kartlegging, workshops, lederstøtte og rapportering for moderne virksomheter. Vi gjør komplekse utfordringer om til målbare tiltak.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link to="/booking">
                <Button size="lg" className="px-10 py-7 text-lg shadow-2xl shadow-indigo-200 uppercase font-black tracking-widest">
                  Book en samtale
                </Button>
              </Link>
              <Link to="/take-assessment">
                <Button variant="secondary" size="lg" className="px-10 py-7 text-lg uppercase font-black tracking-widest">
                  Start kartlegging
                </Button>
              </Link>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-indigo-200/20 rounded-full blur-[100px] -z-10"></div>
            <div className="bg-white p-6 rounded-[48px] shadow-3xl border border-slate-100 relative overflow-hidden group">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1000" 
                alt="Team collaboration" 
                className="rounded-[36px] w-full h-[500px] object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute bottom-12 left-12 right-12 bg-white/90 backdrop-blur-xl p-6 rounded-3xl border border-white shadow-2xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white">
                    <SafeIcon name="Activity" className="text-2xl" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Siste Analyse</p>
                    <p className="text-sm font-bold text-slate-900">+12% forbedring i trygghet</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why this matters */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-black uppercase tracking-tighter leading-none">Hvorfor dette er kritisk for din drift</h2>
              <p className="text-xl text-slate-400 leading-relaxed font-medium">
                Et godt psykososialt arbeidsmiljø bidrar til bedre samarbeid, lavere sykefravær, høyere trivsel og sterkere organisasjonshelse. Det er ikke bare "hyggelig å ha" – det er din viktigste konkurransefordel.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { label: 'Lavere fravær', val: '-15%', icon: 'ArrowDown' },
                { label: 'Høyere trivsel', val: '+28%', icon: 'Smile' },
                { label: 'Økt retention', val: '+22%', icon: 'Users' },
                { label: 'ROI på tiltak', val: '4.2x', icon: 'TrendingUp' }
              ].map((stat, i) => (
                <div key={i} className="p-6 bg-white/5 rounded-3xl border border-white/10">
                  <div className="flex justify-between items-start mb-4">
                    <SafeIcon name={stat.icon} className="text-indigo-400" />
                  </div>
                  <p className="text-3xl font-black mb-1">{stat.val}</p>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Våre Tjenester</h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-lg font-medium">Spesialiserte verktøy for å utvikle fremtidens arbeidsplasser.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Kartlegging av arbeidsmiljø", icon: "Clipboard", desc: "Digital og anonym kartlegging ihht. Arbeidsmiljøloven." },
              { title: "AI-basert analyse", icon: "Cpu", desc: "Dyp innsikt i mønstre, risiko og årsakssammenhenger." },
              { title: "Workshops og fasilitering", icon: "Users", desc: "Interaktive prosesser for varig kulturell endring." },
              { title: "Konfliktmekling", icon: "ShieldAlert", desc: "Profesjonell håndtering av relasjonelle gnisninger." },
              { title: "Lederstøtte", icon: "Award", desc: "Veiledning for å trygge ledere i krevende situasjoner." },
              { title: "Kurs og opplæring", icon: "BookOpen", desc: "Hev kompetansen på alle nivåer i organisasjonen." }
            ].map((service, i) => (
              <Card key={i} hover={true} className="border-none shadow-xl bg-slate-50 group">
                <CardContent className="p-10 space-y-6">
                  <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-500">
                    <SafeIcon name={service.icon} size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 tracking-tight">{service.title}</h3>
                  <p className="text-slate-500 leading-relaxed font-medium">{service.desc}</p>
                  <Button variant="ghost" className="p-0 text-indigo-600 font-black text-[10px] uppercase tracking-widest hover:bg-transparent">
                    Les mer <SafeIcon name="ArrowRight" className="ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Slik Jobber Vi</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { step: '1', title: 'Kartlegging', desc: 'Innsamling av data.' },
              { step: '2', title: 'Analyse', desc: 'AI-drevet tolkning.' },
              { step: '3', title: 'Tiltak', desc: 'Konkrete handlingsmål.' },
              { step: '4', title: 'Workshop', desc: 'Felles forankring.' },
              { step: '5', title: 'Oppfølging', desc: 'Måling av effekt.' }
            ].map((s, i) => (
              <div key={i} className="bg-white p-8 rounded-[40px] shadow-lg border border-slate-100 text-center relative group">
                <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-black mx-auto mb-6 group-hover:bg-indigo-600 transition-colors">
                  {s.step}
                </div>
                <h4 className="font-bold text-slate-900 mb-2">{s.title}</h4>
                <p className="text-xs text-slate-500 font-medium">{s.desc}</p>
                {i < 4 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 translate-y-[-50%] z-10 text-slate-200">
                    <SafeIcon name="ChevronRight" size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-4xl font-black text-slate-900 uppercase tracking-tighter leading-none">Hvem er plattformen for?</h2>
              <div className="space-y-4">
                {[
                  { t: 'Ledere', d: 'Få verktøyene du trenger for å lede teamet ditt trygt.' },
                  { t: 'HR-avdelinger', d: 'Strategisk oversikt over hele organisasjonens helse.' },
                  { t: 'Konsulenter', d: 'Effektiviser dine leveranser med moderne verktøy.' },
                  { t: 'Virksomheter i endring', d: 'Sikre trygghet og kultur under omstillinger.' }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 p-6 rounded-3xl hover:bg-slate-50 transition-colors">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                      <SafeIcon name="Check" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">{item.t}</h4>
                      <p className="text-sm text-slate-500 font-medium">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-indigo-900 rounded-[64px] p-16 text-white relative overflow-hidden">
               <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
               <SafeIcon name="ShieldCheck" className="text-6xl text-indigo-400 mb-8" />
               <h3 className="text-3xl font-black uppercase tracking-tight mb-6 leading-tight">Systematisk og dokumentert HMS-arbeid</h3>
               <p className="text-indigo-100 leading-relaxed text-lg mb-10 font-medium">
                 Vi hjelper dere å oppfylle lovens krav til dokumentasjon og oppfølging, samtidig som vi bygger en kultur folk ønsker å være en del av.
               </p>
               <Link to="/booking">
                 <Button variant="secondary" className="bg-white text-indigo-900 border-none font-black uppercase tracking-widest px-8">
                   Kom i gang i dag
                 </Button>
               </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-indigo-600">
        <div className="max-w-4xl mx-auto px-6 text-center text-white space-y-10">
          <h2 className="text-5xl font-black uppercase tracking-tighter">Klar for en sunnere organisasjon?</h2>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/booking">
              <Button size="lg" className="bg-white text-indigo-600 hover:bg-slate-50 border-none px-12 py-8 text-xl font-black uppercase tracking-widest shadow-2xl">
                Book en Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};